import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Mail, Printer, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  ActionsTd,
  Attention,
  ExpandedRow,
  PayTable,
  SectionHeader,
  SubTabs,
  TableState,
  Td,
  Th,
} from '@/components/admin/PaymentsLayout';
import { cn } from '@/lib/utils';
import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import {
  usePaymentTracking,
  useRunPaperStatements,
  useRunUnappliedNotice,
  useSetPaymentTracking,
  type TrackedAccount,
} from '@/hooks/usePaymentTracking';

/**
 * Payment applications & auto statements — the switch, per account.
 *
 * Ask (Sky, 2026-09-09): "put this in the portal where to track it as a switch
 * … you need to turn it off for Freeflow Beverage Solutions because it's most
 * of the credits."
 *
 * ⚠ THE LIST SHOWS SWITCHED-OFF ACCOUNTS, and accounts that are not portal
 * customers. Everything the customer sees hides both; this is where you turn
 * one back on, so hiding it here would make the switch one-way.
 *
 * ⚠ Turning an account off silences FOUR surfaces at once — the customer's own
 * portal panel, the weekly email, the monthly paper statement, and the chain
 * rollup. It changes nothing in QuickBooks: the money is still there, still
 * unapplied, still on this screen.
 */

function fmtWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** One account: a table row, plus the "stop tracking?" confirmation under it. */
function AccountRow({ a }: { a: TrackedAccount }) {
  const set = useSetPaymentTracking();
  const [note, setNote] = useState('');
  const [asking, setAsking] = useState(false);
  const busy = set.isPending;

  const turnOff = () => {
    set.mutate(
      { qbo_customer_id: a.qbo_customer_id, enabled: false, note: note.trim() || undefined },
      { onSuccess: () => { setAsking(false); setNote(''); } },
    );
  };

  return (
    <>
      <tr className={a.tracking_enabled ? undefined : 'bg-slate-50'}>
        <Td>
          {a.customer_id ? (
            <Link
              to={`/admin/customers/${a.customer_id}/billing`}
              className={cn('font-semibold hover:underline', a.tracking_enabled ? 'text-brix-navy' : 'text-slate-500')}
            >
              {a.name}
            </Link>
          ) : (
            <span className={cn('font-semibold', a.tracking_enabled ? '' : 'text-slate-500')}>{a.name}</span>
          )}
          <span className="block text-[11px] text-brix-muted">
            QuickBooks #{a.qbo_customer_id}
            {!a.in_portal && (
              <span title="No portal account — nothing is emailed to this one regardless; the switch makes that deliberate rather than incidental.">
                {' '}· not in the portal
              </span>
            )}
          </span>
          {!a.tracking_enabled && (a.note || a.updated_by) && (
            <span className="block text-[11px] italic text-slate-500">
              {a.note ? `“${a.note}” — ` : ''}
              {a.updated_by ?? 'unknown'}
              {a.updated_at ? `, ${fmtWhen(a.updated_at)}` : ''}
            </span>
          )}
        </Td>
        <Td right mono className="text-xs">{a.payments || '—'}</Td>
        {/* An account switched off pre-emptively owes nothing yet — it is on this
            list so the switch can be undone, not because there is money to chase. */}
        <Td className="whitespace-nowrap text-xs">
          {a.payments === 0 ? <span className="text-brix-muted">Nothing unapplied right now</span> : fmtMirrorDate(a.oldest)}
        </Td>
        <Td right mono className={a.tracking_enabled ? 'font-semibold' : 'text-slate-500'}>
          {a.payments === 0 ? '—' : formatUsd(a.unapplied)}
        </Td>
        <ActionsTd>
          {a.tracking_enabled ? (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => setAsking((v) => !v)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Stop tracking'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => set.mutate({ qbo_customer_id: a.qbo_customer_id, enabled: true })}
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Track again'}
            </Button>
          )}
        </ActionsTd>
      </tr>

      {((asking && a.tracking_enabled) || set.isError) && (
        <ExpandedRow colSpan={5}>
          {asking && a.tracking_enabled && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs leading-relaxed text-amber-900">
                <span className="font-semibold">{a.name}</span> stops appearing in the weekly email, the
                paper statements, their own portal page and any chain rollup. The{' '}
                {formatUsd(a.unapplied)} stays exactly where it is in QuickBooks, and stays on this list.
              </p>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why (optional) — e.g. intercompany, not a customer credit"
                className="mt-2 w-full rounded-md border border-amber-200 bg-white px-2 py-1.5 text-sm"
              />
              <div className="mt-2 flex gap-2">
                <Button size="sm" disabled={busy} onClick={turnOff}>
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Stop tracking'}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setAsking(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {set.isError && (
            <p className="mt-2 text-xs text-red-600">
              {set.error instanceof Error ? set.error.message : 'could not save'}
            </p>
          )}
        </ExpandedRow>
      )}
    </>
  );
}

/** Tracked accounts with money sitting unapplied — the tab's outstanding count. */
export function unappliedNeedingLook(accounts: TrackedAccount[]): number {
  return accounts.filter((a) => a.tracking_enabled && a.payments > 0).length;
}

export function PaymentTrackingPanel() {
  const query = usePaymentTracking();
  const notice = useRunUnappliedNotice();
  const paper = useRunPaperStatements();
  const [ran, setRan] = useState<string | null>(null);
  const [view, setView] = useState<'tracked' | 'off'>('tracked');

  const accounts = query.data?.accounts ?? [];
  const t = query.data?.totals;
  const tracked = accounts.filter((a) => a.tracking_enabled);
  const off = accounts.filter((a) => !a.tracking_enabled);
  const shown = view === 'tracked' ? tracked : off;
  const outstanding = unappliedNeedingLook(accounts);

  const run = (
    label: string,
    fn: () => Promise<Record<string, unknown>>,
  ) => {
    setRan(null);
    fn()
      .then((r) => setRan(`${label}: ${JSON.stringify(r)}`))
      .catch((e: unknown) => setRan(`${label} failed: ${e instanceof Error ? e.message : String(e)}`));
  };

  return (
    <div className="space-y-3">
      <SectionHeader
        description={
          <>
            Money customers have paid us that is not yet applied to an invoice. A tracked account shows
            its remainder on its own portal page, gets the Friday{' '}
            <span className="font-semibold">“where should we apply this?”</span> email, and gets a paper
            statement on the 8th. Switch one off and all of that stops — the money{' '}
            <span className="font-semibold">does not move</span>, and the account stays on this list.
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              disabled={notice.isPending}
              onClick={() => run('Preview the email', () => notice.mutateAsync({ dry_run: true }))}
            >
              {notice.isPending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              Preview weekly email
            </Button>
            <Button
              variant="secondary"
              disabled={notice.isPending}
              onClick={() => {
                if (!confirm('Send the "where should we apply this?" email now, to every tracked account and the staff list?')) return;
                run('Sent the email', () => notice.mutateAsync({ dry_run: false }));
              }}
            >
              Send it now
            </Button>
            <Button
              variant="secondary"
              disabled={paper.isPending}
              onClick={() => run('Previewed the statements', () => paper.mutateAsync({ mode: 'preview' }))}
            >
              {paper.isPending ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
              Preview paper statements
            </Button>
          </>
        }
      />

      {t && (
        <div className="flex flex-wrap gap-6 rounded-lg border border-brix-border bg-brix-surface-alt px-4 py-3 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-brix-muted">Tracked</div>
            <div className="font-mono font-semibold tabular-nums">{formatUsd(t.tracked_unapplied)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-brix-muted">Not tracked</div>
            <div className="font-mono font-semibold tabular-nums text-slate-500">
              {formatUsd(Number((t.unapplied - t.tracked_unapplied).toFixed(2)))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-brix-muted">Accounts</div>
            <div className="font-semibold tabular-nums">
              {t.tracked_accounts} of {t.accounts}
            </div>
          </div>
        </div>
      )}

      <SubTabs
        tabs={[
          { key: 'tracked', label: 'Tracked', count: tracked.length },
          { key: 'off', label: 'Not tracked', count: off.length },
        ]}
        value={view}
        onChange={setView}
        right={
          <Button size="sm" variant="secondary" onClick={() => query.refetch()} disabled={query.isFetching}>
            {query.isFetching ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </Button>
        }
      />

      {view === 'tracked' && (
        <Attention count={outstanding}>
          tracked accounts have money not applied to an invoice. Apply it in QuickBooks, or let the Friday email ask the customer where it goes.
        </Attention>
      )}

      {ran && (
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-brix-border bg-brix-surface-alt p-3 text-xs">
          {ran}
        </pre>
      )}

      <TableState
        loading={query.isLoading}
        error={query.isError ? query.error : null}
        empty={shown.length === 0}
        emptyText={view === 'tracked' ? 'Nothing unapplied — every payment is on an invoice.' : 'Every account is tracked.'}
      >
        <PayTable minWidth={720}>
          <thead>
            <tr>
              <Th>Customer</Th>
              <Th right>Payments</Th>
              <Th>Oldest</Th>
              <Th right>Unapplied</Th>
              <Th right>Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-brix-border">
            {shown.map((a) => (
              <AccountRow key={a.qbo_customer_id} a={a} />
            ))}
          </tbody>
        </PayTable>
      </TableState>

      {t && t.phantoms_excluded > 0 && (
        <p className="flex items-start gap-2 text-xs text-brix-muted">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
          {t.phantoms_excluded} payment{t.phantoms_excluded === 1 ? ' is' : 's are'} left out as
          suspected double-bookings — money nobody actually holds. That is why this total can differ
          from QuickBooks.
        </p>
      )}
    </div>
  );
}
